import React, { useState, useEffect } from 'react';
import { X, Key, Copy, Check, ShieldAlert, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import ChangePasswordModal from './ChangePasswordModal';

const MyAccountModal = ({ isOpen, onClose }) => {
  const { user, permissions } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Fixed Header */}
        <div className="p-5 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">My Account</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
            aria-label="Close my account"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {user ? (
            <div className="space-y-4">
            {/* User ID */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                User ID
              </label>
              <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                {user.user_id && user.user_key 
                  ? `${user.user_id} - ${user.user_key}`
                  : user.user_key || user.user_id || 'N/A'}
              </div>
            </div>

            {/* Username */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Username
              </label>
              <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                {user.display_name || user.username || 'N/A'}
              </div>
            </div>

            {/* Email */}
            {user.email && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Email
                </label>
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                  {user.email}
                </div>
              </div>
            )}

            {/* Full Name */}
            {(user.first_name || user.last_name) && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Full Name
                </label>
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                  {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                </div>
              </div>
            )}

            {/* Customer */}
            {user.carrier_name && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Customer
                </label>
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                  {user.carrier_name}
                </div>
              </div>
            )}

            {/* User Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                User Type
              </label>
              <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                {user.user_type || 'N/A'}
              </div>
            </div>

            {/* Account Status */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                Status
              </label>
              <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {user.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Authentication Method */}
            {user.auth_method && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Authentication Method
                </label>
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                  {user.auth_method === 'google_oauth' ? 'Google OAuth' : 
                   user.auth_method === 'local' ? 'Local' : user.auth_method}
                </div>
              </div>
            )}

            {/* Created Date */}
            {user.created_at && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Account Created
                </label>
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                  {new Date(user.created_at * 1000).toLocaleDateString()}
                </div>
              </div>
            )}

            {/* Last Login */}
            {user.last_login && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Last Login
                </label>
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm">
                  {new Date(user.last_login * 1000).toLocaleString()}
                </div>
              </div>
            )}

            {/* Permissions */}
            {permissions && permissions.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                  Permissions
                </label>
                <div className="px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 text-sm max-h-48 overflow-y-auto">
                  {permissions.map((permission, index) => {
                    // Determine badge color and label based on source
                    let badgeColor, badgeLabel;
                    if (permission.permission_source === 'organization') {
                      badgeColor = 'bg-blue-100 text-blue-800';
                      badgeLabel = 'Carrier';
                    } else if (permission.permission_source === 'user_type') {
                      badgeColor = 'bg-purple-100 text-purple-800';
                      badgeLabel = 'User Type';
                    } else if (permission.permission_source === 'direct') {
                      badgeColor = 'bg-green-100 text-green-800';
                      badgeLabel = 'Direct';
                    } else {
                      badgeColor = 'bg-gray-100 text-gray-800';
                      badgeLabel = 'Unknown';
                    }

                    return (
                      <div key={index} className="flex items-center justify-between py-1.5 border-b border-gray-200 last:border-0">
                        <span className="text-xs font-medium">{permission.permission_name}</span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badgeColor}`}>
                          {badgeLabel}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  <div className="flex flex-wrap gap-2">
                    <span className="flex items-center gap-1">
                      <span className="inline-block w-2 h-2 rounded-full bg-blue-500"></span>
                      Carrier permissions apply to all users in your organization
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* MCP API Key Section */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <label className="block text-sm font-bold text-gray-800 mb-2 text-left flex items-center gap-2">
                MCP API Key
                {permissions.some(p => p.permission_name === 'mcp.search') ? (
                  <ShieldCheck size={16} className="text-green-600" title="MCP Access Enabled" />
                ) : (
                  <ShieldAlert size={16} className="text-amber-500" title="MCP Permission Missing" />
                )}
              </label>
              
              <McpKeyManager />
            </div>

            {/* Change Password Button - Only show for local authentication users */}
            {user.auth_method === 'local' && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowChangePassword(true)}
                  className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  <Key size={16} className="mr-2" />
                  Change Password
                </button>
              </div>
            )}
            </div>
          ) : (
            <div className="text-center text-gray-500">
              No user information available
            </div>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onSuccess={() => {
          // Optional: Show a success message or perform other actions
          console.log('Password changed successfully');
        }}
      />
    </div>
  );
};

const McpKeyManager = () => {
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const fetchKey = async () => {
    setLoading(true);
    try {
      const data = await api.getMcpKey();
      setApiKey(data.api_key);
      setIsActive(!!data.is_active);
    } catch (err) {
      console.error('Error fetching MCP key:', err);
      setError('Failed to load MCP settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKey();
  }, []);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const data = await api.generateMcpKey();
      if (data.success) {
        setApiKey(data.api_key);
        setIsActive(true);
      }
    } catch (err) {
      setError('Failed to generate key');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate your MCP API Key?')) return;
    setLoading(true);
    try {
      await api.deactivateMcpKey();
      setIsActive(false);
    } catch (err) {
      setError('Failed to deactivate key');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) return <div className="text-sm text-gray-500 italic">Loading MCP settings...</div>;

  return (
    <div className="space-y-3">
      {apiKey && isActive ? (
        <>
          <div className="flex gap-2">
            <div className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-800 text-xs font-mono break-all text-left">
              {apiKey}
            </div>
            <button
              onClick={copyToClipboard}
              className="p-2 text-gray-500 hover:text-blue-600 transition-colors bg-white border border-gray-300 rounded-md"
              title="Copy to clipboard"
            >
              {copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
            </button>
          </div>
          <button
            onClick={handleDeactivate}
            className="text-xs text-red-600 hover:text-red-800 font-medium underline"
          >
            Deactivate Key
          </button>
        </>
      ) : (
        <div className="text-left">
          <p className="text-xs text-gray-500 mb-2">
            No active MCP API Key. Use this key to access the backend lookups via Model Context Protocol.
          </p>
          <button
            onClick={handleGenerate}
            className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
          >
            Request MCP API Key
          </button>
        </div>
      )}
      {error && <div className="text-xs text-red-500 mt-1">{error}</div>}
    </div>
  );
};

export default MyAccountModal;
